import { FormEventHandler, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { Button } from '@bm-js/atlascloud-ui'
import Input from '../components/Input'
import Loader from '../components/Loader'
import Logo from '../components/Logo'
import { breakPointsCss, colors, measurments } from '../utils/config'
import { fetcher } from '../utils/fetcher'
import { useQuery } from '../utils/useQuery'
import { ChangeEvent, UserBM } from '@bm-js/atlascloud-shared'
import { useAuthDispatch, useToast } from '../utils/hooks'
import { AuthActionType } from '../reducers/AuthState.reducer'
import { Title } from 'react-head'

const StyledResetPassword = styled.div`
  .grid {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    .form-container {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
    }
    form,
    .requirements {
      background: white;
      padding: 2.2rem 2.5rem;
      border-radius: ${measurments.borderRadiusLarge}px;
      box-shadow: 0px 32px 64px -12px rgba(16, 24, 40, 0.2);
      min-width: 40ch;
    }
  }
  ul {
    padding-left: 1rem;
  }
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  ${breakPointsCss.tablet} {
    .grid {
      .form-container {
        display: block;
        form {
          margin-bottom: 1rem;
        }
      }
    }
  }
`

const ResetPassword = () => {
  //Static
  const initialUser = {
    password: '',
    passwordRepeat: '',
  }

  //Global
  const navigate = useNavigate()
  const token = useQuery().get('t')
  const toast = useToast()
  const authDispatch = useAuthDispatch()

  //States
  const [formData, setFormData] = useState(initialUser)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [userFromToken, setUserFromToken] = useState<UserBM>()

  //Functions
  const handleChange = (e: ChangeEvent) => {
    setErrorMessage('')
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const loginUser = async () => {
    setErrorMessage('')
    const { err, data } = await fetcher<{ user: UserBM; token: string }>({
      path: 'customer-no-auth/login',
      method: 'POST',
      body: { email: userFromToken!.email, password: formData.password },
    })
    if (err || !data) {
      navigate('/')
      return
    }

    authDispatch({
      type: AuthActionType.replaceState,
      data: { token: data.token },
    })
  }

  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (formData.password !== formData.passwordRepeat) {
      setErrorMessage('Lösenorden stämmer inte överens')
      return
    }
    setLoading(true)
    const { err } = await fetcher({
      path: 'customer-no-auth/reset-password',
      auth: token,
      method: 'POST',
      body: formData,
    })
    if (err) {
      setErrorMessage('Lösenordet uppfyller inte kraven')
      setLoading(false)
      setFormData(initialUser)
      return
    }
    toast.success('Lösenordet har ändrats')
    loginUser()
  }

  const verifyToken = async () => {
    const { err, data } = await fetcher<UserBM>({
      path: 'customer-no-auth/verify-reset-password-token',
      auth: token,
    })
    if (err) {
      setLoading(false)
      return
    }
    setUserFromToken(data)
    setLoading(false)
  }

  useEffect(() => {
    //eslint-disable-next-line
    verifyToken()
    //eslint-disable-next-line
  }, [token])

  if (!userFromToken && loading)
    return (
      <StyledResetPassword>
        <div className='loader-container fdc flex'>
          <p className='primblue s'>Verifierar länk...</p>
          <Loader color={colors.primary400} />
        </div>
      </StyledResetPassword>
    )

  return (
    <StyledResetPassword className='content'>
      <Title>Återställ lösenord | Atlascloud</Title>
      <div className='jcsb ac ptop1 pleft1 flex'>
        <Logo />
      </div>
      <div className='grid'>
        <div className='form-container'>
          <form onSubmit={handleSubmit}>
            <p className='m0 mbottom1 b m primblue'>Återställ lösenord</p>
            <Input
              label='Lösenord'
              desc='Välj ett starkt lösenord'
              required
              disabled={loading}
              type='password'
              value={formData.password}
              name='password'
              onChange={handleChange}
            />
            <Input
              label='Upprepa lösenord'
              desc='Upprepa lösenordet du angivit'
              required
              disabled={loading}
              type='password'
              value={formData.passwordRepeat}
              name='passwordRepeat'
              onChange={handleChange}
            />
            <div className='jce flex'>
              <Button type='submit' disabled={loading}>
                {loading ? 'Återställer...' : 'Återställ'}
              </Button>
            </div>
            {errorMessage && <p className='m0 mtop1 error'>{errorMessage}</p>}
          </form>
          <div className='requirements'>
            <p className='m0 primblue b'>
              Lösenordet måste uppfylla följande krav:
            </p>
            <ul>
              <li>Minst 8 tecken</li>
              <li>Minst en liten bokstav</li>
              <li>Minst en stor bokstav</li>
              <li>Minst en siffra</li>
            </ul>
          </div>
        </div>
      </div>
    </StyledResetPassword>
  )
}

export default ResetPassword
